/**
 * @fileoverview Device Management Dashboard - Manage user's encryption devices
 *
 * This component provides a UI for:
 * 1. Listing all user devices (primary and linked)
 * 2. Showing device status and type
 * 3. Revoking devices
 * 4. Promoting linked devices to primary
 * 5. Adding new devices
 *
 * @module components/DeviceManagementDashboard
 */

import { useState } from 'react';
import { useDeviceManagement } from '../hooks/useDeviceManagement';
import {
  Smartphone,
  Monitor,
  Tablet,
  Plus,
  Trash2,
  ArrowUp,
  Circle,
  CheckCircle,
  XCircle,
  AlertCircle,
} from 'lucide-react';
import type { Device } from '@xanectech/types';
import { createLogger } from '@xanectech/lib';

const logger = createLogger('Auth - DeviceManagementDashboard');

interface DeviceManagementDashboardProps {
  onAddDevice?: () => void;
}

// Get icon for device type (robustly handles string or object info)
function getDeviceIcon(deviceInfo: unknown) {
  // Build a lowercase string from possible fields for matching
  let info = '';
  try {
    if (typeof deviceInfo === 'string') {
      info = deviceInfo.toLowerCase();
    } else if (deviceInfo && typeof deviceInfo === 'object') {
      const obj = deviceInfo as Record<string, unknown>;
      const parts: string[] = [];
      const candidateKeys = [
        'userAgent',
        'ua',
        'device',
        'platform',
        'model',
        'brand',
        'type',
        'os',
        'info',
      ];
      for (const key of candidateKeys) {
        const val = obj[key];
        if (typeof val === 'string') {
          parts.push(val);
        }
      }
      // Fallback to JSON to catch other text
      parts.push(JSON.stringify(obj));
      info = parts.join(' ').toLowerCase();
    }
  } catch {
    info = '';
  }


  if (
    info.includes('phone') ||
    info.includes('mobile') ||
    info.includes('android')
  ) {
    return Smartphone;
  }
  if (info.includes('tablet') || info.includes('ipad')) {
    return Tablet;
  }
  return Monitor;
}

// Get status badge
function getStatusBadge(status: string) {
  switch (status) {
    case 'active':
      return (
        <span className='inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'>
          <CheckCircle className='w-3 h-3' />
          Active
        </span>
      );
    case 'revoked':
      return (
        <span className='inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'>
          <XCircle className='w-3 h-3' />
          Revoked
        </span>
      );
    default:
      return (
        <span className='inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground'>
          <Circle className='w-3 h-3' />
          {status}
        </span>
      );
  }
}

// Device type badge
function getDeviceTypeBadge(deviceType: string) {
  if (deviceType === 'primary') {
    return (
      <span className='inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400'>
        Primary
      </span>
    );
  }
  return (
    <span className='inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'>
      Linked
    </span>
  );
}

// Format date
function formatDate(dateString: string) {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

// Normalize device info into a user-friendly string for rendering
function normalizeDeviceInfo(deviceInfo: unknown): string {
  try {
    if (typeof deviceInfo === 'string') {
      return deviceInfo;
    }
    if (deviceInfo && typeof deviceInfo === 'object') {
      const obj = deviceInfo as Record<string, unknown>;
      const browser = typeof obj.browser === 'string' ? obj.browser : undefined;
      const platform =
        typeof obj.platform === 'string' ? obj.platform : undefined;
      const device = typeof obj.device === 'string' ? obj.device : undefined;
      const model = typeof obj.model === 'string' ? obj.model : undefined;
      const brand = typeof obj.brand === 'string' ? obj.brand : undefined;
      const parts = [platform, browser, brand, model, device].filter(Boolean);
      if (parts.length > 0) {
        return parts.join(' • ');
      }
      // Fallback to JSON string if no known fields present
      return JSON.stringify(obj);
    }
  } catch (error) {
    logger.warn('Failed to normalize device info', {
      error: error instanceof Error ? error.message : String(error),
    });
  }
  return 'Unknown device';
}

export function DeviceManagementDashboard({
  onAddDevice,
}: DeviceManagementDashboardProps) {
  const {
    devices,
    primaryDevices,
    isLoading,
    error,
    revokeDevice,
    promoteToPrimary,
    getActiveDevicesCount,
    clearError,
  } = useDeviceManagement();

  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null);
  const [isRevoking, setIsRevoking] = useState(false);
  const [isPromoting, setIsPromoting] = useState(false);

  // Handle device revocation
  const handleRevoke = async (deviceId: string) => {
    setIsRevoking(true);
    const success = await revokeDevice(deviceId);
    setIsRevoking(false);

    if (success) {
      setConfirmRevoke(null);
    }
  };

  // Handle device promotion
  const handlePromote = async (device: Device) => {
    if (!device.credentialId) {
      return;
    }

    setIsPromoting(true);
    await promoteToPrimary(device.id as string, device.credentialId as string);
    setIsPromoting(false);
  };

  // Render loading state
  if (isLoading && devices.length === 0) {
    return (
      <div className='flex items-center justify-center py-12'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600'></div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div>
          <h2 className='text-2xl font-bold text-foreground'>
            Device Management
          </h2>
          <p className='mt-1 text-sm text-muted-foreground'>
            Manage devices that can access your encrypted data
          </p>
        </div>
        {onAddDevice && (
          <button
            onClick={onAddDevice}
            className='flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium transition-colors'
          >
            <Plus className='w-5 h-5' />
            Add Device
          </button>
        )}
      </div>

      {/* Stats */}
      <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
        <div className='bg-card rounded-lg p-4 border border-border'>
          <div className='text-sm text-muted-foreground'>Total Devices</div>
          <div className='text-2xl font-bold text-foreground mt-1'>
            {getActiveDevicesCount()}
          </div>
        </div>
        <div className='bg-card rounded-lg p-4 border border-border'>
          <div className='text-sm text-muted-foreground'>Primary Devices</div>
          <div className='text-2xl font-bold text-foreground mt-1'>
            {primaryDevices.length}
          </div>
        </div>
        <div className='bg-card rounded-lg p-4 border border-border'>
          <div className='text-sm text-muted-foreground'>Linked Devices</div>
          <div className='text-2xl font-bold text-foreground mt-1'>
            {
              devices.filter(
                (d) => d.deviceType === 'linked' && d.status === 'active',
              ).length
            }
          </div>
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className='bg-destructive/10 dark:bg-destructive/5 border border-destructive/30 rounded-lg p-4'>
          <div className='flex gap-3'>
            <AlertCircle className='w-5 h-5 text-destructive flex-shrink-0' />
            <div className='flex-1'>
              <h4 className='text-sm font-medium text-red-800 dark:text-red-300'>
                Error
              </h4>
              <p className='text-sm text-red-700 dark:text-red-400 mt-1'>
                {error}
              </p>
            </div>
            <button
              onClick={clearError}
              className='text-destructive hover:text-red-800 dark:hover:text-red-300'
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Devices list */}
      {devices.length === 0 ? (
        <div className='bg-card rounded-lg p-12 text-center border border-border'>
          <Smartphone className='w-16 h-16 text-gray-400 mx-auto mb-4' />
          <h3 className='text-lg font-semibold text-foreground mb-2'>
            No devices yet
          </h3>
          <p className='text-muted-foreground mb-4'>
            Get started by adding your first device
          </p>
          {onAddDevice && (
            <button
              onClick={onAddDevice}
              className='inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium transition-colors'
            >
              <Plus className='w-5 h-5' />
              Add Device
            </button>
          )}
        </div>
      ) : (
        <div className='space-y-3'>
          {devices.map((device) => {
            const infoString = normalizeDeviceInfo(device.deviceInfo);
            const DeviceIcon = getDeviceIcon(infoString);
            const isConfirming = confirmRevoke === device.id;

            return (
              <div
                key={device.id}
                className='bg-card rounded-lg p-4 border border-border hover:border-gray-300 dark:hover:border-gray-600 transition-colors'
              >
                <div className='flex items-start gap-4'>
                  {/* Device icon */}
                  <div className='p-3 bg-muted rounded-lg'>
                    <DeviceIcon className='w-6 h-6 text-muted-foreground' />
                  </div>

                  {/* Device info */}
                  <div className='flex-1 min-w-0'>
                    <div className='flex items-start justify-between gap-4'>
                      <div className='min-w-0 flex-1'>
                        <h3 className='text-lg font-semibold text-foreground truncate'>
                          {device.deviceName || 'Unnamed Device'}
                        </h3>
                        <p className='text-sm text-muted-foreground mt-1 truncate'>
                          {infoString || 'No device information'}
                        </p>
                      </div>
                      <div className='flex items-center gap-2'>
                        {getDeviceTypeBadge(device.deviceType as string)}
                        {getStatusBadge(device.status as string)}
                      </div>
                    </div>

                    {/* Device metadata */}
                    <div className='mt-3 flex flex-wrap gap-4 text-xs text-muted-foreground'>
                      <span>
                        Added: {formatDate(device.createdAt as string)}
                      </span>
                      {device.credentialId && (
                        <span className='font-mono'>
                          ID: {device.credentialId.substring(0, 12)}...
                        </span>
                      )}
                    </div>

                    {/* Actions */}
                    {device.status === 'active' && (
                      <div className='mt-4 flex gap-2'>
                        {/* Promote to primary (only for linked devices) */}
                        {device.deviceType === 'linked' &&
                          device.credentialId && (
                            <button
                              onClick={() => handlePromote(device)}
                              disabled={isPromoting}
                              className='inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 rounded-md hover:bg-indigo-100 dark:hover:bg-indigo-900/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                            >
                              <ArrowUp className='w-4 h-4' />
                              Promote to Primary
                            </button>
                          )}

                        {/* Revoke device */}
                        {!isConfirming ? (
                          <button
                            onClick={() =>
                              setConfirmRevoke(device.id as string)
                            }
                            className='inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-md hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors'
                          >
                            <Trash2 className='w-4 h-4' />
                            Revoke
                          </button>
                        ) : (
                          <div className='flex items-center gap-2'>
                            <span className='text-sm text-muted-foreground'>
                              Are you sure?
                            </span>
                            <button
                              onClick={() => handleRevoke(device.id as string)}
                              disabled={isRevoking}
                              className='px-3 py-1.5 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                            >
                              {isRevoking ? 'Revoking...' : 'Yes, Revoke'}
                            </button>
                            <button
                              onClick={() => setConfirmRevoke(null)}
                              disabled={isRevoking}
                              className='px-3 py-1.5 text-sm bg-muted text-foreground/90 rounded-md hover:bg-muted/80 transition-colors'
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Help text */}
      <div className='bg-info/10 dark:bg-info/5 border border-info/30 rounded-lg p-4'>
        <h4 className='text-sm font-medium text-blue-900 dark:text-blue-300 mb-2'>
          About Device Types
        </h4>
        <ul className='space-y-1 text-sm text-blue-800 dark:text-blue-400'>
          <li>
            • <strong>Primary devices</strong> store encryption keys and can
            decrypt data independently
          </li>
          <li>
            • <strong>Linked devices</strong> forward decryption requests to a
            primary device
          </li>
          <li>
            • <strong>Revoking a device</strong> removes its access to your
            encrypted data
          </li>
          <li>• You need at least one primary device to access your data</li>
        </ul>
      </div>
    </div>
  );
}
